import React, { FC, useEffect, useRef } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { format, getHandTopUrl } from '@dt/fe-utils'
import { modalLayer, formatCardNumber, useHistoryNew, toast } from '@utils'
import { ContactType, SendInfoModal, CardEquityModal } from '@cps'
import { storeMobx } from 'restaurant'
import mktController from 'controllers/mktController'
import { memberMobx } from '@mobxStore'
import QRCode from 'qrcode.react'
import JsBarcode from 'jsbarcode'
import MemberApi from '@api/member.api'
import { getGiveAmount } from 'common/utils/member'
import './index.scss'

interface IProps {
  cardInfo: IMemberGiftCardInfo;
}

const GiftCardDetail: FC<IProps> = (props) => {
  const history = useHistoryNew()
  const { cardInfo } = props
  const barcodeRef = useRef<any>(null)

  const goRecharge = () => {
    if (cardInfo.giftcard.status === 'disable' || cardInfo.status === 'disable') {
      toast({ message: i18n.t('mkt.giftCard.gcDisableTip') })
    } else {
      const url: string = `/iGc/recharge/${cardInfo.memberCardId}`
      history.push(url)
    }
  }

  const showCardEquity = (type: 'basicAttr' | 'store') => {
    modalLayer(CardEquityModal, { cardInfo, type, cardType: 'giftcard' }).then(() => { }, () => { })
  }

  const goRecord = (type: 'consume' | 'integral') => {
    history.push(`/iRecord/flow/${type}/${cardInfo.cardNo}`)
  }

  const giveCard = () => {
    modalLayer(ContactType, { title: i18n.t('mkt.giftCard.giveMethod'), hasThird: true }).then((type: any) => {
      if (type === 'wechatMini' && Config.miniVersion >= 102221101) {
        // 小程序
        const shareInfo = {
          mode: 'giftcard',
          ids: [cardInfo.cardNo],
          shareInfo: {
            title: cardInfo.giftcard.giftcardName,
            imageUrl: mktController.getGiftCardCover(cardInfo),
            shape: cardInfo?.extra?.shape || 'rectangle',
            price: cardInfo.balance
          }
        }
        mktController.jumpMiniGive(shareInfo, `${location.origin}/iGc?ct=brand&b=${Config.BRANDID}`)
      } else if (['email', 'mobile'].includes(type)) {
        modalLayer(SendInfoModal, { type: type, title: cardInfo.giftcard.giftcardName, sendInfo: { img: mktController.getGiftCardCover(cardInfo), shape: cardInfo?.extra?.shape || 'rectangle', price: cardInfo.balance } }, 'sendInfoModal').then((val: any) => {
          const para: any = {
            cardNo: cardInfo.cardNo,
            giverName: val.givePerson,
            receiverName: val.receivedPerson,
            message: val.message
          }
          if (type === 'email') {
            para.receiverEmail = val.user
          } else {
            para.receiverMobile = val.user
            para.countryCode = val.countryCode
          }
          memberMobx.giveCard(para).then(() => {
            toast({ message: i18n.t('mkt.giftCard.giveSuccess'), position: 'bottom' })
            mktController.goBack(history)
          })
        })
      } else {
        const para: any = {
          cardNo: cardInfo.cardNo,
          giverName: memberMobx.memberInfo.nickName || '',
          receiverName: '',
          message: i18n.t('mkt.giftCard.sendMessage')
        }
        memberMobx.giveCard(para).then((res: any) => {
          if (res) {
            const url: string = `${getHandTopUrl()}/mmg/#/giftCard/give/${res.cardNo}/${res.giftcardGiveId}?ct=brand&b=${storeMobx.brand.brandId}`
            mktController.jumpMmgUrl(url, i18n.t('mkt.member.sendGcText', { name: para.giverName }), cardInfo.cover)
          }
        })
      }
    })
  }
  const sendEmail = (data: any) => {
    MemberApi.sendEmail({
      nickName: data.nickName,
      to: data.to,
      subject: i18n.t('pageName.giftCard'),
      html: data.html
    }).then(() => {
      toast({ message: i18n.t('mkt.common.giveSucc') })
      mktController.goBack(history)
    })
  }

  useEffect(() => {
    if (cardInfo && barcodeRef.current) {
      JsBarcode(barcodeRef.current, `${cardInfo.cardNo}`, {
        displayValue: false,
        width: 1.5,
        height: 36,
        margin: 8,
        background: 'rgba(244, 244, 244, 0.49)'
      })
    }
  }, [cardInfo, barcodeRef])
  const balance = format.toPrice(cardInfo.balance, 2, false)
  return (
    <>
      <div className='my-giftcard-detail'>
        <div className='giftcard-info'>
          <div className='giftcard-info-top'>
            <div className='giftcard-info-left'>
              <div>{cardInfo.giftcard.giftcardName}</div>
              <div>{formatCardNumber(cardInfo.cardNo)}</div>
              <div>Pin code: {cardInfo.cardPin}</div>
            </div>
            <div data-cypid='mmgGiveGCard' className='giftcard-info-btn' onClick={(e: any) => { e.stopPropagation(); giveCard() }}>{i18n.t('mkt.coupon.giveAway')}</div>
          </div>
          <div className='er-area'>
            <QRCode value={cardInfo.cardNo} size={120} level='H' />
            <div className='bar-area'>
              <svg className='bar-code' ref={barcodeRef} />
            </div>
          </div>
        </div>
        <div className='giftcard-balance-block'>
          <div className='giftcard-balance'>
            <div className='giftcard-balance-content'>
              {i18n.t('mkt.common.balance')}:{format.toCurrency()} <span className='balance'>{balance}</span>
              {(balance.includes('.') && balance.length > 7 || (!balance.includes('.') && balance.length > 4)) ? <div>{getGiveAmount(cardInfo).trim()}</div> : <span>{getGiveAmount(cardInfo)}</span>}
            </div>
            {cardInfo.giftcard.allowAgainLoad && <div data-cypid='mmgRechargeGCard' className='giftcard-info-btn' onClick={(e: any) => { e.stopPropagation(); goRecharge() }}>{i18n.t('mkt.member.rechargeBalance')}</div>}
          </div>
          <div className='expiration-date'>{`${i18n.t('mkt.giftCard.validPeriod')}:${cardInfo.expireTime ? cardInfo.expireTime : i18n.t('mkt.giftCard.useRuleExpired1')}`}</div>
        </div>
        <div className='card-detail-enter-block'>
          <div data-cypid='mmgShowGCardBasic' className='enter-item' onClick={(e: any) => { e.stopPropagation(); showCardEquity('basicAttr') }}>
            <span>{i18n.t('mkt.giftCard.basicAttr')}</span>
            <i className='iconfont icon-chevron-right'></i>
          </div>
          <div data-cypid='mmgToGCardConsume' className='enter-item' onClick={(e: any) => { e.stopPropagation(); goRecord('consume') }}>
            <span>{i18n.t('mkt.member.consumeRecord')}</span>
            <i className='iconfont icon-chevron-right'></i>
          </div>
          <div data-cypid='mmgToGCardStores' className='enter-item' onClick={(e: any) => { e.stopPropagation(); showCardEquity('store') }}>
            <span>{i18n.t('mkt.member.applyStore')}</span>
            <i className='iconfont icon-chevron-right'></i>
          </div>
        </div>
      </div>
    </>
  )
}

export default observer(GiftCardDetail)
